import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { formatTier, tierKey } from "../lib/tierLabel";
import * as d3 from "d3";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { RefreshCw, Layers, X, Filter, Search } from "lucide-react";
import { Button } from "../components/ui/button";

// ── Type & Color Configuration ──
const NODE_TYPES = {
  process: { color: "#6366f1", label: "Process", icon: "P" },
  vendor: { color: "#10b981", label: "Vendor", icon: "V" },
  facility: { color: "#f59e0b", label: "Facility", icon: "F" },
  application: { color: "#06b6d4", label: "Application", icon: "A" },
  asset: { color: "#64748b", label: "IT Asset", icon: "S" },
  it_system: { color: "#64748b", label: "IT System", icon: "S" },
  equipment: { color: "#78716c", label: "Equipment", icon: "E" },
  people: { color: "#ec4899", label: "Team / Role", icon: "T" },
  infrastructure: { color: "#7c3aed", label: "Infrastructure", icon: "I" },
  other: { color: "#94a3b8", label: "Other", icon: "O" },
};

const TIER_COLORS = {
  tier_1: { color: "#ef4444", label: "Tier 1 (Critical)" },
  tier_2: { color: "#f59e0b", label: "Tier 2 (Important)" },
  tier_3: { color: "#10b981", label: "Tier 3 (Standard)" },
  none: { color: "#94a3b8", label: "No Tier" },
};

const EDGE_LABEL_MAP = {
  "depends on": { key: "depends", c: "#ef4444", l: "DEPENDS", d: false },
  "feeds": { key: "feeds", c: "#ec4899", l: "FEEDS", d: false },
  "supplies": { key: "supplies", c: "#10b981", l: "SUPPLIES", d: false },
  "uses": { key: "uses", c: "#f59e0b", l: "USES", d: true },
  "powers": { key: "powers", c: "#06b6d4", l: "POWERS", d: false },
  "hosted by": { key: "hosted", c: "#10b981", l: "HOSTED BY", d: false },
  "runs on": { key: "runs_on", c: "#06b6d4", l: "RUNS ON", d: false },
  "triggers": { key: "triggers", c: "#8b5cf6", l: "TRIGGERS", d: false },
  "supports": { key: "supports", c: "#14b8a6", l: "SUPPORTS", d: true },
  "operates": { key: "operates", c: "#64748b", l: "OPERATES", d: true },
  "audits": { key: "audits", c: "#0ea5e9", l: "AUDITS", d: true },
  "located": { key: "located", c: "#f59e0b", l: "LOCATED", d: true },
  // Dependency type labels (from typed upstream/downstream)
  "data/info": { key: "data_info", c: "#06b6d4", l: "DATA/INFO", d: false },
  "tech/system": { key: "tech_system", c: "#8b5cf6", l: "TECH/SYS", d: false },
  "personnel": { key: "personnel", c: "#ec4899", l: "PERSONNEL", d: false },
  "financial": { key: "financial", c: "#10b981", l: "FINANCIAL", d: false },
  "regulatory": { key: "regulatory", c: "#f59e0b", l: "REGULATORY", d: false },
  "infrastructure": { key: "infrastructure", c: "#78716c", l: "INFRA", d: false },
  "supply chain": { key: "supply_chain", c: "#14b8a6", l: "SUPPLY CHAIN", d: false },
  "comms/approval": { key: "comms_approval", c: "#6366f1", l: "COMMS", d: false },
  "operational": { key: "operational", c: "#0ea5e9", l: "OPERATIONAL", d: false },
  "other": { key: "other", c: "#94a3b8", l: "OTHER", d: true },
};

function getEdgeType(label) {
  if (!label) return { key: "unknown", c: "#94a3b8", l: "LINKED", d: false };
  const lower = label.toLowerCase().trim();
  return EDGE_LABEL_MAP[lower] || { key: lower.replace(/\s+/g, "_"), c: "#94a3b8", l: label.toUpperCase(), d: false };
}

// ── Data Transform ──
function transformData(rawNodes, rawEdges) {
  const nodeMap = {};
  const d3Nodes = rawNodes.map((n) => {
    const d = n.data || {};
    const nodeType = d.nodeType || "other";
    const tier = d.tier || "none";
    const node = {
      id: n.id,
      name: d.label || n.id,
      type: nodeType,
      tier: tier,
      rto: d.rto_hours ? `${d.rto_hours}h` : null,
      dept: d.department || null,
      criticality: d.criticality || null,
      deploy: d.deployment_type || null,
      facilityType: d.facility_type || null,
      vendorType: d.vendor_type || null,
      city: d.city || null,
      remote: !!d.remote,
      infraClass: d.infra_class || null,
      infraIcon: d.icon || null,
      severityColor: d.infra_severity_color || null,
      assetType: d.nodeType === "it_system" || d.nodeType === "equipment" ? d.nodeType : null,
      r: 16,
    };
    nodeMap[n.id] = node;
    return node;
  });

  const d3Links = rawEdges
    .filter((e) => nodeMap[e.source] && nodeMap[e.target])
    .map((e) => {
      const et = getEdgeType(e.label);
      const critData = e.data || {};
      const isLocated = critData.edge_kind === "located_at";
      return {
        source: e.source,
        target: e.target,
        type: isLocated ? "located_at" : et.key,
        label: isLocated ? (e.label ? e.label.toUpperCase() : "LOCATED AT") : et.l,
        color: e.style?.stroke || (isLocated ? "#94a3b8" : et.c),
        dashed: isLocated ? true : et.d,
        strokeWidth: e.style?.strokeWidth || (isLocated ? 1.5 : 2),
        criticality: critData.criticality || null,
        dependencyType: critData.dependency_type || null,
      };
    });

  // Compute degree for sizing
  const deg = {};
  d3Links.forEach((l) => {
    deg[l.source] = (deg[l.source] || 0) + 1;
    deg[l.target] = (deg[l.target] || 0) + 1;
  });
  d3Nodes.forEach((n) => {
    n.r = 14 + (deg[n.id] || 0) * 2.5;
  });

  return { d3Nodes, d3Links, deg };
}

// ── Main Component ──
export default function DependencyGraph({
  nodes: initialNodes = [],
  edges: initialEdges = [],
  summary = {},
  loading = false,
  onRefresh,
  onNodeClick,
  title = "Dependency Map",
  height = "600px",
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simRef = useRef(null);
  const flowTimerRef = useRef(null);
  const gRef = useRef(null);
  const zoomRef = useRef(null);

  // Filter state
  const [filters, setFilters] = useState({ nodeType: {}, tier: {}, depType: {} });
  const [showFilters, setShowFilters] = useState(true);
  const [layout, setLayout] = useState("force");
  const [showNames, setShowNames] = useState(true);
  const [showAttrs, setShowAttrs] = useState(true);
  const [showEdgeLabels, setShowEdgeLabels] = useState(true);
  const [showFlow, setShowFlow] = useState(true);
  const [showTiers, setShowTiers] = useState(true);
  const [detailNode, setDetailNode] = useState(null);
  const [liveStats, setLiveStats] = useState({ nodes: 0, edges: 0 });

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHighlightId, setSearchHighlightId] = useState(null);
  const searchInputRef = useRef(null);

  // Transform data
  const { d3Nodes, d3Links, deg } = useMemo(
    () => transformData(initialNodes, initialEdges),
    [initialNodes, initialEdges]
  );

  // Collect unique types from data
  const uniqueNodeTypes = useMemo(() => {
    const s = new Set();
    d3Nodes.forEach((n) => s.add(n.type));
    return [...s];
  }, [d3Nodes]);

  const uniqueTiers = useMemo(() => {
    const s = new Set();
    d3Nodes.forEach((n) => s.add(n.tier || "none"));
    return [...s];
  }, [d3Nodes]);

  const uniqueDepTypes = useMemo(() => {
    const s = new Set();
    d3Links.forEach((l) => s.add(l.type));
    return [...s];
  }, [d3Links]);

  // Initialize filters when data changes
  useEffect(() => {
    const nt = {};
    uniqueNodeTypes.forEach((t) => (nt[t] = true));
    const ti = {};
    uniqueTiers.forEach((t) => (ti[t] = true));
    const dt = {};
    uniqueDepTypes.forEach((t) => (dt[t] = true));
    setFilters({ nodeType: nt, tier: ti, depType: dt });
  }, [uniqueNodeTypes, uniqueTiers, uniqueDepTypes]);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return d3Nodes
      .filter((n) => n.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [searchQuery, d3Nodes]);

  // Zoom to a specific node
  const zoomToNode = useCallback((node) => {
    const svg = svgRef.current;
    if (!svg || !zoomRef.current) return;
    const svgSel = d3.select(svg);
    const scale = 1.8;
    const transform = d3.zoomIdentity
      .translate(svg.__W / 2, svg.__H / 2)
      .scale(scale)
      .translate(-node.x, -node.y);
    svgSel.transition().duration(600).call(zoomRef.current.transform, transform);
  }, []);

  // Handle search selection
  const handleSearchSelect = useCallback((node) => {
    setSearchHighlightId(node.id);
    setSearchQuery(node.name);
    setSearchOpen(false);
    setDetailNode(node);
    zoomToNode(node);
  }, [zoomToNode]);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchHighlightId(null);
    setSearchOpen(false);
  }, []);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      const container = document.querySelector('[data-testid="graph-search-container"]');
      if (container && !container.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Filter logic
  const isNodeVisible = useCallback(
    (n) => {
      if (filters.nodeType[n.type] === false) return false;
      if (filters.tier[n.tier || "none"] === false) return false;
      return true;
    },
    [filters]
  );

  const isLinkVisible = useCallback(
    (l, nodesArr) => {
      if (filters.depType[l.type] === false) return false;
      const sid = typeof l.source === "object" ? l.source.id : l.source;
      const tid = typeof l.target === "object" ? l.target.id : l.target;
      const sn = nodesArr.find((n) => n.id === sid);
      const tn = nodesArr.find((n) => n.id === tid);
      if (sn && !isNodeVisible(sn)) return false;
      if (tn && !isNodeVisible(tn)) return false;
      return true;
    },
    [filters, isNodeVisible]
  );

  // ── D3 Rendering ──
  useEffect(() => {
    if (d3Nodes.length === 0) return;

    const container = containerRef.current;
    if (!container) return;
    const W = container.clientWidth;
    const H = container.clientHeight;

    // Clear
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current).attr("width", W).attr("height", H);

    // Defs for arrow markers
    const defs = svg.append("defs");
    d3Links.forEach((l) => {
      const markerId = `arrow-${l.type}-${l.color.replace("#", "")}`;
      if (!defs.select(`#${markerId}`).node()) {
        defs
          .append("marker")
          .attr("id", markerId)
          .attr("viewBox", "0 -5 10 10")
          .attr("refX", 10)
          .attr("refY", 0)
          .attr("markerWidth", 8)
          .attr("markerHeight", 8)
          .attr("orient", "auto")
          .append("path")
          .attr("d", "M0,-4L10,0L0,4Z")
          .attr("fill", l.color);
      }
    });

    const g = svg.append("g");
    gRef.current = g;

    // Zoom
    const zoomBehavior = d3
      .zoom()
      .scaleExtent([0.12, 4])
      .on("zoom", (e) => g.attr("transform", e.transform));
    svg.call(zoomBehavior);
    zoomRef.current = zoomBehavior;

    // Click on canvas background clears search highlight
    svg.on("click", (e) => {
      if (e.target === svgRef.current) {
        setSearchHighlightId(null);
      }
    });

    // Groups
    const edgeG = g.append("g").attr("class", "edges");
    const particleG = g.append("g").attr("class", "particles");
    const nodeG = g.append("g").attr("class", "nodes");

    // Simulation
    const sim = d3
      .forceSimulation(d3Nodes)
      .force(
        "link",
        d3.forceLink(d3Links).id((d) => d.id).distance(120)
      )
      .force("charge", d3.forceManyBody().strength(-600))
      .force("center", d3.forceCenter(W / 2, H / 2))
      .force(
        "collision",
        d3.forceCollide().radius((d) => d.r + 30)
      )
      .on("tick", tick);

    simRef.current = sim;

    let highlightNode = null;

    function getLinked(nid) {
      const ns = new Set([nid]);
      const es = new Set();
      d3Links.forEach((l, i) => {
        if (!isLinkVisible(l, d3Nodes)) return;
        const si = typeof l.source === "object" ? l.source.id : l.source;
        const ti = typeof l.target === "object" ? l.target.id : l.target;
        if (si === nid || ti === nid) {
          ns.add(si);
          ns.add(ti);
          es.add(i);
        }
      });
      return { ns, es };
    }

    function tick() {
      const activeHighlight = highlightNode || (searchHighlightId ? d3Nodes.find(n => n.id === searchHighlightId) : null);
      const linked = activeHighlight ? getLinked(activeHighlight.id) : null;

      // ── Edges ──
      const eSel = edgeG.selectAll(".edge-group").data(d3Links, (_, i) => i);
      const eEnter = eSel.enter().append("g").attr("class", "edge-group");
      eEnter.append("path").attr("class", "edge-path");
      eEnter.append("rect").attr("class", "edge-label-bg");
      eEnter.append("text").attr("class", "edge-label-text");
      const eMerge = eEnter.merge(eSel);

      eMerge.each(function (d, i) {
        const grp = d3.select(this);
        const vis = isLinkVisible(d, d3Nodes);
        grp.attr("display", vis ? null : "none");
        if (!vis) return;

        const sx = d.source.x, sy = d.source.y, tx = d.target.x, ty = d.target.y;
        const dx = tx - sx, dy = ty - sy, dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const sr = d.source.r + 4, tr = d.target.r + 12;
        const nx = dx / dist, ny = dy / dist;
        const sX = sx + nx * sr, sY = sy + ny * sr, eX = tx - nx * tr, eY = ty - ny * tr;
        const curve = 25;
        const mx = (sX + eX) / 2 - ny * curve, my = (sY + eY) / 2 + nx * curve;
        const path = `M${sX},${sY} Q${mx},${my} ${eX},${eY}`;
        const isDimmed = linked && !linked.es.has(i);

        const markerId = `arrow-${d.type}-${d.color.replace("#", "")}`;

        grp
          .select(".edge-path")
          .attr("d", path)
          .attr("stroke", d.color)
          .attr("stroke-width", d.strokeWidth || 1.8)
          .attr("fill", "none")
          .attr("stroke-linecap", "round")
          .attr("marker-end", `url(#${markerId})`)
          .attr("stroke-dasharray", d.dashed ? "6,3" : null)
          .attr("opacity", isDimmed ? 0.06 : 0.7);

        const lx = 0.25 * sX + 0.5 * mx + 0.25 * eX;
        const ly = 0.25 * sY + 0.5 * my + 0.25 * eY;

        if (showEdgeLabels && !isDimmed) {
          const text = grp
            .select(".edge-label-text")
            .text(d.label)
            .attr("x", lx)
            .attr("y", ly + 3.5)
            .attr("text-anchor", "middle")
            .attr("font-size", "8px")
            .attr("font-weight", "700")
            .attr("fill", "#fff")
            .attr("font-family", "Inter, sans-serif")
            .attr("letter-spacing", "0.3px")
            .attr("pointer-events", "none")
            .attr("opacity", 1);
          const bb = text.node().getBBox();
          grp
            .select(".edge-label-bg")
            .attr("x", bb.x - 3)
            .attr("y", bb.y - 1)
            .attr("width", bb.width + 6)
            .attr("height", bb.height + 2)
            .attr("rx", 4)
            .attr("ry", 4)
            .attr("fill", d.color)
            .attr("opacity", 0.9);
        } else {
          grp.select(".edge-label-text").attr("opacity", 0);
          grp.select(".edge-label-bg").attr("opacity", 0);
        }

        d.__path = { sX, sY, mx, my, eX, eY };
      });
      eSel.exit().remove();

      // ── Nodes ──
      const nSel = nodeG.selectAll(".node-group").data(d3Nodes, (d) => d.id);
      const nEnter = nSel.enter().append("g").attr("class", "node-group").attr("cursor", "pointer");
      nEnter.append("circle").attr("class", "tier-ring");
      nEnter.append("circle").attr("class", "node-circle");
      nEnter
        .append("text")
        .attr("class", "node-icon")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("fill", "#fff")
        .attr("font-weight", "800")
        .attr("font-family", "Plus Jakarta Sans, Inter, sans-serif")
        .attr("pointer-events", "none");
      nEnter.append("text").attr("class", "node-name").attr("text-anchor", "middle");
      nEnter.append("text").attr("class", "node-type-label").attr("text-anchor", "middle");
      nEnter.append("text").attr("class", "node-attr a1").attr("text-anchor", "middle");
      nEnter.append("text").attr("class", "node-attr a2").attr("text-anchor", "middle");

      // Drag
      nEnter.call(
        d3
          .drag()
          .on("start", (e, d) => {
            if (!e.active) sim.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (e, d) => {
            d.fx = e.x;
            d.fy = e.y;
          })
          .on("end", (e, d) => {
            if (!e.active) sim.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          })
      );

      nEnter
        .on("mouseenter", (_, d) => {
          highlightNode = d;
          tick();
        })
        .on("mouseleave", () => {
          highlightNode = null;
          tick();
        })
        .on("click", (_, d) => {
          setDetailNode(d);
          if (onNodeClick) onNodeClick({ id: d.id, data: d });
        });

      const nMerge = nEnter.merge(nSel);
      nMerge.each(function (d) {
        const grp = d3.select(this);
        const vis = isNodeVisible(d);
        grp.attr("display", vis ? null : "none");
        if (!vis) return;

        const nt = NODE_TYPES[d.type] || NODE_TYPES.other;
        const isDimmed = linked && !linked.ns.has(d.id);
        grp.attr("transform", `translate(${d.x},${d.y})`);
        grp.attr("opacity", isDimmed ? 0.15 : 1);

        const tc = TIER_COLORS[d.tier];
        grp
          .select(".tier-ring")
          .attr("r", showTiers && tc ? d.r + 5 : 0)
          .attr("fill", "none")
          .attr("stroke", tc ? tc.color : "none")
          .attr("stroke-width", 3)
          .attr("stroke-dasharray", "4,2")
          .attr("opacity", showTiers && tc && !isDimmed ? 0.6 : 0);

        grp
          .select(".node-circle")
          .attr("r", d.r)
          .attr("fill", d.severityColor || nt.color)
          .attr("stroke", isDimmed ? "#ddd" : d3.color(d.severityColor || nt.color).darker(0.3))
          .attr("stroke-width", 2.5)
          .style("transition", "r 0.2s");

        grp
          .select(".node-icon")
          .text(nt.icon)
          .attr("font-size", d.r * 0.7 + "px");

        grp
          .select(".node-name")
          .text(showNames ? d.name : "")
          .attr("y", d.r + 14)
          .attr("font-size", "10.5px")
          .attr("font-weight", "700")
          .attr("font-family", "Plus Jakarta Sans, Inter, sans-serif")
          .attr("fill", "#1A2B3C")
          .attr("pointer-events", "none");

        grp
          .select(".node-type-label")
          .text(showNames ? nt.label : "")
          .attr("y", d.r + 25)
          .attr("font-size", "7.5px")
          .attr("font-weight", "600")
          .attr("font-family", "Inter, sans-serif")
          .attr("fill", "#7A9AAF")
          .attr("text-transform", "uppercase")
          .attr("letter-spacing", "0.4px")
          .attr("pointer-events", "none");

        let a1 = "";
        if (showAttrs) {
          if (d.remote) a1 = "⚡ remote";
          else if (d.rto) a1 = "RTO: " + d.rto;
          else if (d.criticality) a1 = d.criticality;
          else if (d.deploy) a1 = d.deploy;
          else if (d.assetType) a1 = d.assetType;
          else if (d.city) a1 = d.city;
        }
        grp
          .select(".a1")
          .text(a1)
          .attr("y", d.r + 36)
          .attr("font-size", "8px")
          .attr("fill", nt.color)
          .attr("font-weight", "700")
          .attr("font-family", "Inter, sans-serif")
          .attr("pointer-events", "none");

        let a2 = "";
        if (showAttrs) {
          if (d.dept) a2 = d.dept;
          else if (tierKey(d.tier)) a2 = formatTier(d.tier).toUpperCase();
        }
        grp
          .select(".a2")
          .text(a2)
          .attr("y", a1 ? d.r + 46 : 0)
          .attr("font-size", "7.5px")
          .attr("fill", "#7a9aaf")
          .attr("font-weight", "600")
          .attr("font-family", "Inter, sans-serif")
          .attr("pointer-events", "none");
      });
      nSel.exit().remove();

      // Update stats
      const vn = d3Nodes.filter(isNodeVisible).length;
      const vl = d3Links.filter((l) => isLinkVisible(l, d3Nodes)).length;
      setLiveStats({ nodes: vn, edges: vl });
    }

    // ── Flow Particles ──
    function startFlow() {
      stopFlow();
      const pd = [];
      d3Links.forEach((l, i) => {
        if (!isLinkVisible(l, d3Nodes)) return;
        pd.push({ li: i, t: 0, sp: 0.008 + Math.random() * 0.004 });
        pd.push({ li: i, t: 0.5, sp: 0.008 + Math.random() * 0.004 });
      });
      particleG.selectAll(".flow-particle").remove();
      const ps = particleG
        .selectAll(".flow-particle")
        .data(pd)
        .enter()
        .append("circle")
        .attr("class", "flow-particle")
        .attr("r", 2.5)
        .attr("pointer-events", "none");

      function anim() {
        ps.each(function (d) {
          d.t = (d.t + d.sp) % 1;
          const lk = d3Links[d.li];
          const p = lk.__path;
          if (!p) return;
          const t = d.t;
          const x = (1 - t) * (1 - t) * p.sX + 2 * (1 - t) * t * p.mx + t * t * p.eX;
          const y = (1 - t) * (1 - t) * p.sY + 2 * (1 - t) * t * p.my + t * t * p.eY;
          d3.select(this).attr("cx", x).attr("cy", y).attr("fill", lk.color).attr("opacity", 0.8);
        });
        flowTimerRef.current = requestAnimationFrame(anim);
      }
      anim();
    }

    function stopFlow() {
      if (flowTimerRef.current) cancelAnimationFrame(flowTimerRef.current);
      particleG.selectAll(".flow-particle").remove();
    }

    if (showFlow) {
      setTimeout(startFlow, 1500);
    }

    // Store for layout changes
    svgRef.current.__sim = sim;
    svgRef.current.__nodes = d3Nodes;
    svgRef.current.__links = d3Links;
    svgRef.current.__deg = deg;
    svgRef.current.__startFlow = startFlow;
    svgRef.current.__stopFlow = stopFlow;
    svgRef.current.__W = W;
    svgRef.current.__H = H;

    return () => {
      stopFlow();
      sim.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d3Nodes, d3Links, isNodeVisible, isLinkVisible, showNames, showAttrs, showEdgeLabels, showFlow, showTiers, searchHighlightId]);

  // ── Layout Switching ──
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !svg.__sim) return;
    const sim = svg.__sim;
    const nodes = svg.__nodes;
    const links = svg.__links;
    const degLocal = svg.__deg;
    const W = svg.__W;
    const H = svg.__H;
    const cx = W / 2, cy = H / 2;

    switch (layout) {
      case "force":
        sim
          .force("link", d3.forceLink(links).id((d) => d.id).distance(120))
          .force("charge", d3.forceManyBody().strength(-600))
          .force("center", d3.forceCenter(cx, cy))
          .force("x", null)
          .force("y", null);
        nodes.forEach((n) => { n.fx = null; n.fy = null; });
        sim.alpha(1).restart();
        break;
      case "hierarchical": {
        const typeOrder = ["process", "application", "vendor", "facility", "asset", "it_system", "equipment", "other"];
        const buckets = {};
        nodes.forEach((n) => {
          if (!buckets[n.type]) buckets[n.type] = [];
          buckets[n.type].push(n);
        });
        typeOrder.forEach((t, ti) => {
          (buckets[t] || []).forEach((n, ni) => {
            n.fx = cx + (ni - (buckets[t] || []).length / 2) * 130;
            n.fy = 80 + ti * 120;
          });
        });
        sim.alpha(0.5).restart();
        break;
      }
      case "radial": {
        const center = nodes.reduce((m, n) => ((degLocal[n.id] || 0) > (degLocal[m.id] || 0) ? n : m), nodes[0]);
        if (!center) break;
        const dist2 = {};
        dist2[center.id] = 0;
        const queue = [center.id];
        const visited = new Set([center.id]);
        while (queue.length) {
          const c = queue.shift();
          links.forEach((l) => {
            const si = typeof l.source === "object" ? l.source.id : l.source;
            const ti = typeof l.target === "object" ? l.target.id : l.target;
            const nb = si === c ? ti : ti === c ? si : null;
            if (nb && !visited.has(nb)) {
              visited.add(nb);
              dist2[nb] = dist2[c] + 1;
              queue.push(nb);
            }
          });
        }
        const rings = {};
        nodes.forEach((n) => {
          if (n.id !== center.id) {
            const d = dist2[n.id] || 3;
            if (!rings[d]) rings[d] = [];
            rings[d].push(n);
          }
        });
        center.fx = cx;
        center.fy = cy;
        Object.entries(rings).forEach(([d, arr]) => {
          const r = d * 130;
          arr.forEach((n, i) => {
            const a = (2 * Math.PI * i) / arr.length - Math.PI / 2;
            n.fx = cx + r * Math.cos(a);
            n.fy = cy + r * Math.sin(a);
          });
        });
        sim.alpha(0.5).restart();
        break;
      }
      case "circular": {
        const N = nodes.length;
        const typeGroups = {};
        nodes.forEach((n) => {
          if (!typeGroups[n.type]) typeGroups[n.type] = [];
          typeGroups[n.type].push(n);
        });
        let idx = 0;
        Object.values(typeGroups).forEach((gr) => {
          gr.forEach((n) => {
            const a = (2 * Math.PI * idx) / N - Math.PI / 2;
            const r = Math.min(cx, cy) * 0.7;
            n.fx = cx + r * Math.cos(a);
            n.fy = cy + r * Math.sin(a);
            idx++;
          });
        });
        sim.alpha(0.5).restart();
        break;
      }
      case "structural": {
        // B4 — fixed top-to-bottom bands: org/dept → processes → teams/apps/equipment → vendors → facilities & infrastructure (substrate)
        const bands = [
          ["organization", "org_unit", "department"],
          ["process"],
          ["people", "application", "it_system", "asset", "equipment"],
          ["vendor"],
          ["facility", "infrastructure"],
        ];
        const bandOf = (t) => {
          const i = bands.findIndex((b) => b.includes(t));
          return i === -1 ? 2 : i;
        };
        const buckets = {};
        nodes.forEach((n) => {
          const b = bandOf(n.type);
          (buckets[b] = buckets[b] || []).push(n);
        });
        const H = svg.__H;
        const bandCount = bands.length;
        Object.entries(buckets).forEach(([b, arr]) => {
          const y = 70 + (Number(b) * (H - 140)) / Math.max(1, bandCount - 1);
          arr.forEach((n, ni) => {
            n.fx = cx + (ni - arr.length / 2) * 130;
            n.fy = y;
          });
        });
        sim.alpha(0.5).restart();
        break;
      }
      default:
        break;
    }
  }, [layout]);

  // ── Filter Toggles ──
  const toggleFilter = useCallback((group, key) => {
    setFilters((prev) => ({
      ...prev,
      [group]: { ...prev[group], [key]: !prev[group][key] },
    }));
  }, []);

  const toggleGroup = useCallback((group) => {
    setFilters((prev) => {
      const f = prev[group];
      const allOn = Object.values(f).every((v) => v);
      const updated = {};
      Object.keys(f).forEach((k) => (updated[k] = !allOn));
      return { ...prev, [group]: updated };
    });
  }, []);

  const resetAllFilters = useCallback(() => {
    setFilters((prev) => {
      const nt = {};
      Object.keys(prev.nodeType).forEach((k) => (nt[k] = true));
      const ti = {};
      Object.keys(prev.tier).forEach((k) => (ti[k] = true));
      const dt = {};
      Object.keys(prev.depType).forEach((k) => (dt[k] = true));
      return { nodeType: nt, tier: ti, depType: dt };
    });
  }, []);

  // ── Detail Panel Data ──
  const detailConnections = useMemo(() => {
    if (!detailNode) return [];
    return d3Links
      .filter((l) => {
        const si = typeof l.source === "object" ? l.source.id : l.source;
        const ti = typeof l.target === "object" ? l.target.id : l.target;
        return si === detailNode.id || ti === detailNode.id;
      })
      .map((l) => {
        const si = typeof l.source === "object" ? l.source.id : l.source;
        const ti = typeof l.target === "object" ? l.target.id : l.target;
        const isOut = si === detailNode.id;
        const connId = isOut ? ti : si;
        const conn = d3Nodes.find((n) => n.id === connId);
        return { conn, label: l.label, color: l.color, dir: isOut ? "\u2192" : "\u2190" };
      })
      .filter((c) => c.conn);
  }, [detailNode, d3Links, d3Nodes]);

  // ── Loading / Empty States ──
  if (loading) {
    return (
      <Card data-testid="dependency-graph-loading">
        <CardContent className="flex items-center justify-center" style={{ height }}>
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-500">Loading dependency graph...</span>
        </CardContent>
      </Card>
    );
  }

  if (initialNodes.length === 0) {
    return (
      <Card data-testid="dependency-graph-empty">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center text-gray-500" style={{ height: "300px" }}>
          <Layers className="h-12 w-12 mb-4 text-gray-300" />
          <p>No dependencies to display</p>
          <p className="text-sm text-gray-400 mt-1">Add processes and assets to see the dependency map</p>
        </CardContent>
      </Card>
    );
  }

  const layoutOptions = ["force", "hierarchical", "radial", "circular", "structural"];

  return (
    <Card className="overflow-hidden" data-testid="dependency-graph">
      {/* Top Controls Bar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b bg-white flex-wrap" data-testid="graph-controls-bar">
        <span className="text-[0.65rem] font-bold text-gray-400 uppercase tracking-wider">Layout</span>
        {layoutOptions.map((l) => (
          <button
            key={l}
            data-testid={`layout-btn-${l}`}
            onClick={() => setLayout(l)}
            className={`text-xs font-semibold px-3 py-1 rounded-md border transition-all ${
              layout === l
                ? "bg-[#0B6E99] text-white border-[#0B6E99]"
                : "bg-white text-gray-500 border-gray-200 hover:border-[#0B6E99] hover:text-[#0B6E99]"
            }`}
          >
            {l.charAt(0).toUpperCase() + l.slice(1)}
          </button>
        ))}

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <span className="text-[0.65rem] font-bold text-gray-400 uppercase tracking-wider">Show</span>
        {[
          { key: "names", val: showNames, set: setShowNames, color: "#0B6E99" },
          { key: "attrs", val: showAttrs, set: setShowAttrs, color: "#f59e0b" },
          { key: "edgeLabels", val: showEdgeLabels, set: setShowEdgeLabels, color: "#ef4444" },
          { key: "flow", val: showFlow, set: setShowFlow, color: "#8b5cf6" },
          { key: "tiers", val: showTiers, set: setShowTiers, color: "#ef4444" },
        ].map((t) => (
          <label key={t.key} className="flex items-center gap-1 text-xs font-medium text-gray-500 cursor-pointer select-none" data-testid={`toggle-${t.key}`}>
            <input
              type="checkbox"
              checked={t.val}
              onChange={() => t.set(!t.val)}
              className="accent-[#0B6E99] w-3 h-3"
            />
            <div className="w-2 h-2 rounded-full" style={{ background: t.color }} />
            {t.key.charAt(0).toUpperCase() + t.key.slice(1).replace(/([A-Z])/g, " $1")}
          </label>
        ))}

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Search */}
        <div className="relative" data-testid="graph-search-container">
          <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-md px-2 py-1 focus-within:border-[#0B6E99] focus-within:ring-1 focus-within:ring-[#0B6E99]/20 transition-all">
            <Search className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
              onFocus={() => { if (searchQuery.trim()) setSearchOpen(true); }}
              onKeyDown={(e) => {
                if (e.key === "Escape") { clearSearch(); searchInputRef.current?.blur(); }
                if (e.key === "Enter" && searchResults.length > 0) handleSearchSelect(searchResults[0]);
              }}
              placeholder="Search nodes..."
              className="bg-transparent border-none outline-none text-xs w-28 placeholder:text-gray-400 text-gray-700"
              data-testid="graph-search-input"
            />
            {searchQuery && (
              <button onClick={clearSearch} className="text-gray-400 hover:text-gray-600" data-testid="graph-search-clear">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          {searchOpen && searchResults.length > 0 && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-56 overflow-y-auto" data-testid="graph-search-dropdown">
              {searchResults.map((node) => {
                const nt = NODE_TYPES[node.type] || NODE_TYPES.other;
                return (
                  <button
                    key={node.id}
                    onClick={() => handleSearchSelect(node)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left transition-colors border-b border-gray-50 last:border-0"
                    data-testid={`search-result-${node.id}`}
                  >
                    <div className="w-5 h-5 rounded flex items-center justify-center text-[0.5rem] font-extrabold text-white flex-shrink-0" style={{ background: nt.color }}>
                      {nt.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-gray-800 truncate">{node.name}</div>
                      <div className="text-[0.6rem] text-gray-400">{nt.label}{node.dept ? ` · ${node.dept}` : ""}{node.rto ? ` · RTO ${node.rto}` : ""}</div>
                    </div>
                    {node.tier && node.tier !== "none" && (
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: (TIER_COLORS[node.tier] || TIER_COLORS.none).color }} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {searchOpen && searchQuery.trim() && searchResults.length === 0 && (
            <div className="absolute top-full left-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 px-3 py-2 text-xs text-gray-400" data-testid="graph-search-no-results">
              No nodes found
            </div>
          )}
        </div>

        <div className="ml-auto flex items-center gap-3 text-xs text-gray-500">
          <span>
            <span className="font-bold text-[#0B6E99]" data-testid="stat-nodes">{liveStats.nodes}</span> Nodes
          </span>
          <span>
            <span className="font-bold text-[#0B6E99]" data-testid="stat-edges">{liveStats.edges}</span> Edges
          </span>
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh} className="h-7 text-xs" data-testid="refresh-graph-btn">
              <RefreshCw className="h-3 w-3 mr-1" />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Graph Area */}
      <CardContent className="p-0 relative" style={{ height }}>
        <div ref={containerRef} className="w-full h-full bg-[#F5F8FA]" data-testid="d3-graph-container">
          <svg
            ref={svgRef}
            className="w-full h-full"
            style={{ cursor: "grab" }}
          />
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div
            className="absolute top-3 left-3 z-10 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-md w-52 max-h-[calc(100%-24px)] overflow-y-auto"
            data-testid="filter-panel"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b">
              <span className="text-xs font-bold text-gray-800 uppercase tracking-wide">Filters</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={resetAllFilters}
                  className="text-[0.62rem] font-semibold text-[#0B6E99] hover:bg-blue-50 px-1.5 py-0.5 rounded"
                  data-testid="reset-filters-btn"
                >
                  Reset All
                </button>
                <button onClick={() => setShowFilters(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Node Types */}
            <FilterSection
              title="Node Types"
              onToggleAll={() => toggleGroup("nodeType")}
              items={uniqueNodeTypes.map((t) => ({
                key: t,
                label: (NODE_TYPES[t] || NODE_TYPES.other).label,
                color: (NODE_TYPES[t] || NODE_TYPES.other).color,
                count: d3Nodes.filter((n) => n.type === t).length,
                active: filters.nodeType[t] !== false,
                dotType: "solid",
              }))}
              onToggle={(key) => toggleFilter("nodeType", key)}
            />

            {/* Tiers */}
            <FilterSection
              title="Criticality Tier"
              onToggleAll={() => toggleGroup("tier")}
              items={uniqueTiers.map((t) => ({
                key: t,
                label: (TIER_COLORS[t] || TIER_COLORS.none).label,
                color: (TIER_COLORS[t] || TIER_COLORS.none).color,
                count: d3Nodes.filter((n) => (n.tier || "none") === t).length,
                active: filters.tier[t] !== false,
                dotType: "ring",
              }))}
              onToggle={(key) => toggleFilter("tier", key)}
            />

            {/* Dependency Types */}
            <FilterSection
              title="Dependency Types"
              onToggleAll={() => toggleGroup("depType")}
              items={uniqueDepTypes.map((t) => {
                const match = Object.values(EDGE_LABEL_MAP).find((e) => e.key === t);
                return {
                  key: t,
                  label: match ? match.l : t.toUpperCase(),
                  color: match ? match.c : "#94a3b8",
                  count: d3Links.filter((l) => l.type === t).length,
                  active: filters.depType[t] !== false,
                  dotType: "bar",
                };
              })}
              onToggle={(key) => toggleFilter("depType", key)}
            />

            {/* Edge style + infrastructure legend (B1) */}
            <div className="px-2 py-1.5 border-t border-gray-100 mt-1" data-testid="edge-style-legend">
              <div className="text-[0.58rem] font-bold text-gray-400 uppercase tracking-wider mb-1">Edge Style</div>
              <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                <svg width="26" height="6"><line x1="0" y1="3" x2="26" y2="3" stroke="#64748b" strokeWidth="2" /></svg>
                <span>solid = functional dependency</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-600 mb-1.5">
                <svg width="26" height="6"><line x1="0" y1="3" x2="26" y2="3" stroke="#94a3b8" strokeWidth="2" strokeDasharray="6 4" /></svg>
                <span>dashed = located / hosted at</span>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[0.68rem] text-gray-500">
                <span>🏛️ Datacenter</span>
                <span>☁️ Cloud</span>
                <span>🏭 Vendor Site</span>
                <span>🌐 SaaS</span>
                <span>👥 Team / Role</span>
              </div>
            </div>
          </div>
        )}

        {/* Show Filters Button (when panel hidden) */}
        {!showFilters && (
          <button
            onClick={() => setShowFilters(true)}
            className="absolute top-3 left-3 z-10 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm px-3 py-2 flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:border-[#0B6E99] hover:text-[#0B6E99] transition-all"
            data-testid="show-filters-btn"
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        )}

        {/* Detail Panel */}
        {detailNode && (
          <DetailPanel
            node={detailNode}
            connections={detailConnections}
            links={d3Links}
            nodes={d3Nodes}
            onClose={() => setDetailNode(null)}
          />
        )}

        {/* Hints */}
        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-1.5 text-[0.66rem] text-gray-400 flex gap-3 shadow-sm" data-testid="graph-hints">
          <span><b className="text-gray-500">Drag node</b> Reposition</span>
          <span><b className="text-gray-500">Click node</b> Details</span>
          <span><b className="text-gray-500">Hover</b> Highlight path</span>
          <span><b className="text-gray-500">Scroll</b> Zoom</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Filter Section Sub-component ──
function FilterSection({ title, onToggleAll, items, onToggle }) {
  return (
    <>
      <div className="px-3 py-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[0.62rem] font-bold text-gray-400 uppercase tracking-wide">{title}</span>
          <button
            onClick={onToggleAll}
            className="text-[0.58rem] font-semibold text-[#0B6E99] hover:bg-blue-50 px-1 py-0.5 rounded"
          >
            Toggle All
          </button>
        </div>
        {items.map((item) => (
          <div
            key={item.key}
            onClick={() => onToggle(item.key)}
            className={`flex items-center gap-1.5 px-2 py-1 mb-0.5 rounded-md cursor-pointer transition-all text-xs font-medium select-none ${
              item.active ? "text-gray-600 hover:bg-gray-100" : "opacity-35 line-through text-gray-400"
            }`}
            data-testid={`filter-chip-${item.key}`}
          >
            {item.dotType === "solid" && (
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: item.active ? item.color : "#bbb" }} />
            )}
            {item.dotType === "ring" && (
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{
                  background: "transparent",
                  border: `2.5px solid ${item.active ? item.color : "#bbb"}`,
                }}
              />
            )}
            {item.dotType === "bar" && (
              <div className="w-3.5 h-1 rounded-sm flex-shrink-0" style={{ background: item.active ? item.color : "#bbb" }} />
            )}
            <span className="flex-1 min-w-0 truncate">{item.label}</span>
            <span className="text-[0.62rem] font-bold text-gray-400 min-w-[14px] text-right">{item.count}</span>
          </div>
        ))}
      </div>
      <div className="h-px bg-gray-200" />
    </>
  );
}

// ── Detail Panel Sub-component ──
function DetailPanel({ node, connections, links, nodes, onClose }) {
  const nt = NODE_TYPES[node.type] || NODE_TYPES.other;
  const inDeg = links.filter((l) => (typeof l.target === "object" ? l.target.id : l.target) === node.id).length;
  const outDeg = links.filter((l) => (typeof l.source === "object" ? l.source.id : l.source) === node.id).length;

  return (
    <div
      className="absolute top-3 right-3 z-10 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg p-4 w-72 animate-in slide-in-from-right-2"
      data-testid="detail-panel"
    >
      <button
        onClick={onClose}
        className="absolute top-2 right-2 w-5 h-5 bg-gray-100 rounded flex items-center justify-center text-gray-400 hover:bg-gray-200 hover:text-gray-600"
        data-testid="close-detail-btn"
      >
        <X className="h-3 w-3" />
      </button>

      <div className="flex items-center gap-2.5 mb-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-extrabold"
          style={{ background: nt.color + "18", color: nt.color }}
        >
          {nt.icon}
        </div>
        <div>
          <div className="font-bold text-sm text-gray-800" data-testid="detail-node-name">{node.name}</div>
          <div className="text-[0.65rem] font-semibold text-gray-400 uppercase tracking-wide">
            {nt.label}
            {node.dept && <span> &middot; {node.dept}</span>}
            {node.city && <span> &middot; {node.city}</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5 mb-3">
        {node.rto && (
          <div className="bg-gray-50 rounded-md px-2 py-1.5">
            <div className="text-[0.6rem] text-gray-400">RTO</div>
            <div className="text-sm font-bold text-gray-800">{node.rto}</div>
          </div>
        )}
        {node.tier && node.tier !== "none" && (
          <div className="bg-gray-50 rounded-md px-2 py-1.5">
            <div className="text-[0.6rem] text-gray-400">Tier</div>
            <div className="text-sm font-bold text-gray-800">{formatTier(node.tier).toUpperCase()}</div>
          </div>
        )}
        {node.criticality && (
          <div className="bg-gray-50 rounded-md px-2 py-1.5">
            <div className="text-[0.6rem] text-gray-400">Criticality</div>
            <div className="text-sm font-bold text-gray-800">{node.criticality}</div>
          </div>
        )}
        {node.deploy && (
          <div className="bg-gray-50 rounded-md px-2 py-1.5">
            <div className="text-[0.6rem] text-gray-400">Deploy</div>
            <div className="text-sm font-bold text-gray-800">{node.deploy}</div>
          </div>
        )}
        <div className="bg-gray-50 rounded-md px-2 py-1.5">
          <div className="text-[0.6rem] text-gray-400">Depends On</div>
          <div className="text-sm font-bold text-gray-800">{outDeg}</div>
        </div>
        <div className="bg-gray-50 rounded-md px-2 py-1.5">
          <div className="text-[0.6rem] text-gray-400">Depended By</div>
          <div className="text-sm font-bold text-gray-800">{inDeg}</div>
        </div>
      </div>

      {connections.length > 0 && (
        <div>
          <div className="text-[0.65rem] font-semibold text-gray-500 mb-1">Connections:</div>
          <div className="max-h-40 overflow-y-auto">
            {connections.map((c, i) => {
              const cnt = NODE_TYPES[c.conn.type] || NODE_TYPES.other;
              return (
                <div key={i} className="flex items-center gap-1.5 py-1 border-b border-gray-100 last:border-0 text-xs text-gray-500">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cnt.color }} />
                  <span className="flex-1 truncate">{c.conn.name}</span>
                  <span
                    className="text-[0.52rem] font-bold text-white px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{ background: c.color }}
                  >
                    {c.dir} {c.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
